'use client';

import { useEffect, useRef } from 'react';
import { useSession, setActiveOrganization } from '@/lib/platform/auth/client';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/platform/hooks/queries/query-keys';
import { logger } from '@/lib/platform/utils/logger';
import { fetchWithBypass } from '@/lib/platform/utils/fetch';

/**
 * DataPrefetcher Component
 *
 * Prefetches critical data after login to improve navigation performance.
 * This runs in the background and doesn't block rendering.
 *
 * IMPORTANT: Also ensures activeOrganizationId is set in Better Auth session
 * after login. This is required for organization-based features like Sponsorships.
 */
export function DataPrefetcher() {
  const { data: session, isPending } = useSession();
  const queryClient = useQueryClient();
  const hasSetActiveOrg = useRef(false);

  useEffect(() => {
    // Only prefetch after session is loaded and user is logged in
    if (isPending || !session?.user) {
      return;
    }

    // Get active organization ID from session
    const organizationId = session.session?.activeOrganizationId;

    // FIX: If no activeOrganizationId in session, fetch user's organizations and set the first one
    // This is needed because Better Auth doesn't automatically set activeOrganizationId on normal login
    // (it's only set during onboarding via setActiveOrganization call)
    if (!organizationId && !hasSetActiveOrg.current) {
      logger.log(
        '[DataPrefetcher] 🔴 No activeOrganizationId in session, fetching organizations...'
      );
      hasSetActiveOrg.current = true;

      // Use React Query to fetch organizations (shares cache, prevents duplicate fetches)
      queryClient
        .fetchQuery({
          queryKey: queryKeys.clubs.lists(),
          queryFn: async () => {
            const response = await fetchWithBypass('/api/organizations');
            if (!response.ok) throw new Error('Failed to fetch organizations');
            const data = await response.json();
            return data.organizations || [];
          },
          staleTime: 5 * 60 * 1000, // 5 minutes
        })
        .then(async (clubs) => {
          logger.log(
            '[DataPrefetcher] 📋 Found',
            clubs.length,
            'organizations:',
            clubs.map((c: Record<string, unknown>) => c.name).join(', ')
          );

          if (clubs.length > 0) {
            // Set first organization as active
            const firstOrganizationId = clubs[0].id;
            logger.log(
              `[DataPrefetcher] 🎯 Setting first organization as active: ${clubs[0].name} (${firstOrganizationId})`
            );

            // Call Better Auth to set activeOrganizationId in session
            const result = await setActiveOrganization({ organizationId: firstOrganizationId });

            logger.log('[DataPrefetcher] 📡 setActiveOrganization result:', result);

            if (result.error) {
              logger.error('[DataPrefetcher] ❌ Failed to set active organization:', result.error);
            } else {
              logger.log('[DataPrefetcher] ✅ Active organization set successfully!');

              // FIX: Use invalidateQueries with refetchType: 'none' to update cache without refetching
              // This prevents re-render of all components using session query
              queryClient.invalidateQueries({
                queryKey: ['session'],
                refetchType: 'none', // Update cache metadata without triggering refetch
              });
              logger.log('[DataPrefetcher] 🔄 Session cache updated (no refetch)...');
            }
          } else {
            logger.log('[DataPrefetcher] ⚠️ User has no organizations');
          }
        })
        .catch((error) => {
          logger.error('[DataPrefetcher] ❌ Error setting active organization:', error);
        });

      // Skip prefetch on this run - will run again after session is refetched
      return;
    }

    // Skip prefetch if no active organization (e.g., new user just verified email)
    if (!organizationId) {
      logger.log('[DataPrefetcher] No active organization, skipping permissions prefetch');
      return;
    }

    // Prefetch permissions (needed for navigation)
    queryClient.prefetchQuery({
      queryKey: queryKeys.permissions.user(session.user.id, organizationId),
      queryFn: async () => {
        const response = await fetchWithBypass(`/api/permissions?organizationId=${organizationId}`);
        if (!response.ok) throw new Error('Failed to fetch permissions');
        return response.json();
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    });

    // Prefetch clubs list (needed for sidebar)
    queryClient.prefetchQuery({
      queryKey: queryKeys.clubs.lists(),
      queryFn: async () => {
        const response = await fetchWithBypass('/api/organizations');
        if (!response.ok) throw new Error('Failed to fetch organizations');
        const data = await response.json();
        return data.organizations || [];
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    });

    logger.log('[DataPrefetcher] Prefetched permissions and clubs');
  }, [session, isPending, queryClient]);

  return null; // This component doesn't render anything
}
